// Where Clerk may send the browser back to after sign-in.
//
// The signed-out dashboard renders the sign-in route for every path, and the sign-in
// route asks Clerk to return to the page it started on. A path the signed-in app has
// no route for (a WSM 5.0 bookmark like /account/login, a typo) survives that round trip
// and lands on the 404 page the moment sign-in succeeds, which reads as sign-in having
// failed. So the return address is the starting page only when the signed-in app serves
// it; otherwise it is the dashboard root, the same choice the password routes make.
//
// This mirrors the top-level `SectionRoute`s and redirects of the authenticated router
// in src/index.tsx, which cannot be imported from here (it imports this module's
// neighbours). A new section there is a new prefix here, or its sign-in round trip
// falls back to the root.
import { AppSections } from "@dashboard/apps/urls";
import { attributeSection } from "@dashboard/attributes/urls";
import { channelsSection } from "@dashboard/channels/urls";
import { checkoutListPath } from "@dashboard/checkouts/urls";
import { cmsPagesSection } from "@dashboard/cmsPages/urls";
import { getAppMountUri } from "@dashboard/config";
import { CustomAppSections } from "@dashboard/custom-apps/urls";
import { envSettingsSection } from "@dashboard/envSettings/urls";
import { extensionsSection } from "@dashboard/extensions/urls";
import { giftCardsSectionUrlName } from "@dashboard/giftCards/urls";
import { htmlManagerSection } from "@dashboard/html-manager/urls";
import { modelingSection } from "@dashboard/modeling/urls";
import { modelTypesPath } from "@dashboard/modelTypes/urls";
import { wizardBasePath } from "@dashboard/onboardingWizard/urls";
import { pagesSection } from "@dashboard/pages/urls";
import { pluginSection } from "@dashboard/plugins/urls";
import { shippingSection } from "@dashboard/shipping/urls";
import { navigationSection } from "@dashboard/structures/urls";
import { taxSection } from "@dashboard/taxes/urls";
import { warehouseSection } from "@dashboard/warehouses/urls";
import { wsmComposeSectionUrlName } from "@dashboard/wsmCompose/urls";
import { wsmDealerSectionUrlName } from "@dashboard/wsmDealer/urls";
import { wsmFitmentSectionUrlName } from "@dashboard/wsmFitment/urls";
import { wsmKitsSectionUrlName } from "@dashboard/wsmKits/urls";
import { wsmSeriesSectionUrlName } from "@dashboard/wsmSeries/urls";

/** Top-level path prefixes the signed-in router serves, redirects included. */
export const servedSectionPaths: readonly string[] = [
  "/analytics",
  AppSections.appsSection,
  attributeSection,
  "/billing",
  "/categories",
  channelsSection,
  checkoutListPath,
  cmsPagesSection,
  "/collections",
  "/configuration",
  CustomAppSections.appsSection,
  "/customers",
  "/discounts",
  envSettingsSection,
  extensionsSection,
  giftCardsSectionUrlName,
  htmlManagerSection,
  "/inquiry-forms",
  modelingSection,
  modelTypesPath,
  "/orders",
  pagesSection,
  "/permission-groups",
  pluginSection,
  "/product-types",
  "/products",
  "/search",
  shippingSection,
  "/site-settings",
  "/staff",
  navigationSection,
  taxSection,
  "/translations",
  warehouseSection,
  wizardBasePath,
  "/wsm-ai",
  wsmComposeSectionUrlName,
  wsmDealerSectionUrlName,
  wsmFitmentSectionUrlName,
  wsmKitsSectionUrlName,
  wsmSeriesSectionUrlName,
  // legacyRedirects and the inline <Redirect>s in src/index.tsx
  "/pages",
  "/page-types",
  "/navigation",
  "/fitment-management",
  "/storefront-search-settings",
];

const trimSlashes = (path: string) => path.replace(/^\/+|\/+$/g, "");

/**
 * True when the signed-in app has a route for `pathname` (the root included), so it is
 * somewhere Clerk can usefully send the browser back to. A prefix matches at a segment
 * boundary: /orders/123 is served, /ordersx is not.
 */
export const isServedPath = (pathname: string): boolean => {
  const mount = trimSlashes(getAppMountUri());
  let path = trimSlashes(pathname);

  if (mount) {
    if (path !== mount && !path.startsWith(mount + "/")) {
      return false;
    }

    path = trimSlashes(path.slice(mount.length));
  }

  if (path === "") {
    return true;
  }

  return servedSectionPaths.some(section => {
    const prefix = trimSlashes(section);

    return path === prefix || path.startsWith(prefix + "/");
  });
};
